import React, { useContext, useMemo, useCallback, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Box,
  Tooltip,
  Skeleton,
  Button,
  CardActions
} from '@mui/material';
import { motion } from 'framer-motion';
import { Compare, Delete, Visibility, TrendingUp, TrendingDown, Remove } from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';

/**
 * Updated impact calculation that works with ROIAnalyzer data structure
 */
const calculateScenarioImpact = (scenario, currencySymbol = '₹') => {
  if (!scenario) return 0;
  
  // If totalImpact is already calculated and stored, use it
  if (typeof scenario.totalImpact === 'number' && !isNaN(scenario.totalImpact)) {
    return scenario.totalImpact;
  }
  
  // Calculate from values structure (current ROIAnalyzer format)
  if (scenario.values && typeof scenario.values === 'object') {
    try {
      let total = 0;
      // For now, return 0 as we don't have the calculation context here
      // The actual calculation should be done in ROIAnalyzer and stored in totalImpact
      return total;
    } catch (error) {
      console.error('Error calculating impact from values:', error);
      return 0;
    }
  }
  
  // Fallback to 0 if no impact data found
  return 0;
};

/**
 * Count factors with non-zero targets from values object
 */
const countActiveFactors = (scenario) => {
  if (!scenario?.values) return 0;
  
  try {
    return Object.values(scenario.values).filter(value => 
      value && typeof value.target === 'number' && value.target > 0
    ).length;
  } catch (error) {
    return 0;
  }
};

/**
 * Sub-components for better organization
 */
const ScenarioCardSkeleton = ({ testId }) => (
  <Card 
    data-testid={`${testId}-loading`}
    sx={{ 
      border: '1px solid #e0e0e0',
      height: 200
    }}
  >
    <CardContent>
      <Skeleton variant="text" width="60%" height={28} />
      <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="circular" width={40} height={40} />
      </Box>
    </CardContent>
  </Card>
);

const ScenarioCardError = ({ testId }) => (
  <Card 
    data-testid={`${testId}-error`}
    sx={{ 
      border: '1px solid #ffebee',
      backgroundColor: '#fff5f5'
    }}
  >
    <CardContent>
      <Typography variant="h6" color="error" gutterBottom>
        Failed to load scenario
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Please try refreshing the page or contact support.
      </Typography>
    </CardContent>
  </Card>
);

const ScenarioHeader = ({ scenario, activeFactorsCount }) => (
  <>
    <Typography 
      variant="h6" 
      gutterBottom 
      sx={{ 
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
      title={scenario.name}
    >
      {scenario.name || 'Unnamed Scenario'}
    </Typography>
    
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {scenario.industry || 'No Industry'} • {scenario.tier || 'No Tier'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {activeFactorsCount} active factor{activeFactorsCount !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </Box>
  </>
);

const ImpactDisplay = ({ 
  totalImpact, 
  impactColor, 
  impactVariant, 
  impactIcon, 
  formatImpact,
  currencySymbol
}) => (
  <Tooltip title={`Total impact potential: ${currencySymbol}${Math.abs(totalImpact).toLocaleString()}`}>
    <Chip 
      icon={impactIcon}
      label={formatImpact(totalImpact)}
      color={impactColor}
      variant={impactVariant}
      size="medium"
      sx={{
        fontWeight: 'bold',
        minWidth: 100,
        justifyContent: 'center',
        fontSize: '0.9rem',
        mb: 2
      }}
    />
  </Tooltip>
);

const ScenarioAdditionalInfo = ({ scenario }) => (
  (scenario.description || scenario.tags?.length > 0) && (
    <Box sx={{ mb: 2 }}>
      {scenario.description && (
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 1
          }}
        >
          {scenario.description}
        </Typography>
      )}
      
      {scenario.tags?.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {scenario.tags.slice(0, 3).map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          ))}
          {scenario.tags.length > 3 && (
            <Chip
              label={`+${scenario.tags.length - 3}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>
      )}
    </Box>
  )
);

const ActionButtons = ({ scenario, isSelected, user, onSelect, onDelete, onViewDetails }) => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    pt: 1,
    borderTop: '1px solid #f0f0f0'
  }}>
    <Tooltip title={isSelected ? 'Deselect scenario' : 'Select for comparison'}>
      <IconButton 
        onClick={onSelect}
        color={isSelected ? 'primary' : 'default'}
        size="medium"
        aria-label={isSelected ? 'Deselect scenario' : 'Select scenario for comparison'}
      >
        <Compare />
      </IconButton>
    </Tooltip>
    
    <Box>
      <Tooltip title="View scenario details">
        <IconButton 
          onClick={onViewDetails}
          size="medium"
          aria-label="View scenario details"
        >
          <Visibility />
        </IconButton>
      </Tooltip>
      
      {user && (
        <Tooltip title="Delete scenario">
          <IconButton 
            onClick={onDelete}
            color="error"
            size="medium"
            aria-label="Delete scenario"
          >
            <Delete />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </Box>
);

/**
 * Updated ScenarioCard component that works with ROIAnalyzer data structure
 */
export const ScenarioCard = ({
  scenario,
  onSelect,
  onDelete,
  onViewDetails,
  isSelected = false,
  isLoading = false,
  testId = 'scenario-card',
  currencySymbol = '₹' // Default to Indian Rupee
}) => {
  const { user } = useContext(AuthContext);

  // Handlers with validation
  const handleSelect = useCallback(() => {
    if (scenario?._id && typeof onSelect === 'function') {
      onSelect(scenario._id);
    }
  }, [scenario, onSelect]);

  const handleDelete = useCallback(() => {
    if (scenario?._id && typeof onDelete === 'function') {
      onDelete(scenario._id);
    }
  }, [scenario, onDelete]);

  const handleViewDetails = useCallback(() => {
    console.log("👁️ ScenarioCard View Details clicked for:", scenario?.name);
    if (scenario?._id && typeof onViewDetails === 'function') {
      onViewDetails(scenario);
    }
  }, [scenario, onViewDetails]);

  // Memoized calculation of total impact
  const { totalImpact, impactColor, impactVariant, impactIcon } = useMemo(() => {
    const impact = calculateScenarioImpact(scenario, currencySymbol);
    
    let color = 'default';
    let variant = 'outlined';
    let IconComponent = <Remove fontSize="small" />;

    if (impact > 0) {
      color = 'success';
      variant = 'filled';
      IconComponent = <TrendingUp fontSize="small" />;
    } else if (impact < 0) {
      color = 'error';
      variant = 'filled';
      IconComponent = <TrendingDown fontSize="small" />;
    }

    return {
      totalImpact: impact,
      impactColor: color,
      impactVariant: variant,
      impactIcon: IconComponent
    };
  }, [scenario, currencySymbol]);

  // Count active factors
  const activeFactorsCount = useMemo(() => {
    return countActiveFactors(scenario);
  }, [scenario]);

  // Format impact with currency symbol
  const formatImpact = useCallback((amount) => {
    const absAmount = Math.abs(amount);
    
    if (absAmount >= 1000000) {
      return `${currencySymbol}${(absAmount / 1000000).toFixed(1)}M`;
    } else if (absAmount >= 1000) {
      return `${currencySymbol}${(absAmount / 1000).toFixed(1)}K`;
    } else if (absAmount > 0) {
      return `${currencySymbol}${absAmount.toLocaleString()}`;
    }
    return `${currencySymbol}0`;
  }, [currencySymbol]);

  // Loading state
  if (isLoading) {
    return <ScenarioCardSkeleton testId={testId} />;
  }

  // Error state
  if (!scenario) {
    return <ScenarioCardError testId={testId} />;
  }

  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
      whileTap={{ scale: 0.98 }}
    >
      <Card
        sx={{
          border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
          backgroundColor: isSelected ? '#f5f9ff' : 'background.paper',
          transition: 'all 0.3s ease-in-out',
          position: 'relative',
          overflow: 'visible',
          height: '100%',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        {isSelected && (
          <Chip
            label="Selected"
            color="primary"
            size="small"
            sx={{
              position: 'absolute',
              top: -10,
              right: 10,
              fontWeight: 'bold',
              zIndex: 1
            }}
          />
        )}
        
        <CardContent>
          <ScenarioHeader 
            scenario={scenario} 
            activeFactorsCount={activeFactorsCount} 
          />
          
          <ImpactDisplay
            totalImpact={totalImpact}
            impactColor={impactColor}
            impactVariant={impactVariant}
            impactIcon={impactIcon}
            formatImpact={formatImpact}
            currencySymbol={currencySymbol}
          />
          
          <ScenarioAdditionalInfo scenario={scenario} />
          
          <ActionButtons
            scenario={scenario}
            isSelected={isSelected}
            user={user}
            onSelect={handleSelect}
            onDelete={handleDelete}
            onViewDetails={handleViewDetails}
          />
        </CardContent>
      </Card>
    </motion.div>
  );
};

ScenarioCard.defaultProps = {
  scenario: null,
  isSelected: false,
  isLoading: false,
  testId: 'scenario-card',
  currencySymbol: '₹'
};

export default ScenarioCard;